import React from 'react';

/**
 * Compensation Ladder — a 4-step CTC progression view:
 *   Current → Expected → Offered → Joined
 *
 * Inputs are flexible: pass numeric `currentL` / `expectedL` (already in
 * Lakhs) and the candidate's `apps` array from /candidates/:id/applications-
 * summary (where `offered_ctc` is stored in rupees, so we /100000 here).
 *
 * Auto-hides when fewer than 2 steps are populated — single-data candidates
 * don't get a panel full of dashes.
 *
 * Visible only to roles allowed by the parent (we don't gate here; the
 * caller decides whether to mount the component based on canSeeRevenue).
 */
const STEP_COLOR = {
  current: 'bg-slate-100 text-slate-700',
  expected: 'bg-sky-100 text-sky-800',
  offered: 'bg-teal-100 text-teal-800',
  joined: 'bg-emerald-100 text-emerald-800',
};

const CompensationLadder = ({ currentL = 0, expectedL = 0, apps = [], compact = false }) => {
  const offerApps = (apps || []).filter(
    (a) => (a.offered_ctc || 0) > 0 &&
      ['offer_released', 'joined', 'interview_selected'].includes(a.stage)
  );
  const offeredL = offerApps.length
    ? Math.max(...offerApps.map((a) => Number(a.offered_ctc) || 0)) / 100000
    : 0;
  const joinedApp = (apps || []).find(
    (a) => a.stage === 'joined' && (a.offered_ctc || 0) > 0
  );
  const joinedL = joinedApp ? (Number(joinedApp.offered_ctc) || 0) / 100000 : 0;

  const steps = [
    { key: 'current', label: 'Current', value: Number(currentL) || 0 },
    { key: 'expected', label: 'Expected', value: Number(expectedL) || 0 },
    { key: 'offered', label: 'Offered', value: offeredL },
    { key: 'joined', label: 'Joined', value: joinedL },
  ];
  const populated = steps.filter((s) => s.value > 0).length;
  if (populated < 2) return null;

  const expectedDelta = currentL && expectedL ? expectedL - currentL : null;
  const offeredVsExpected = expectedL && offeredL ? offeredL - expectedL : null;
  const finalDelta = currentL && joinedL ? joinedL - currentL : null;
  const pctOfCurrent = (delta) =>
    currentL ? Math.round((delta / currentL) * 100) : null;

  return (
    <div
      className={`border border-emerald-200 bg-emerald-50/40 rounded-lg ${compact ? 'p-3' : 'p-4'}`}
      data-testid="comp-ladder"
    >
      <h4 className={`font-medium text-slate-900 ${compact ? 'mb-2 text-xs' : 'mb-3'} flex items-center gap-1.5`}>
        <span className="text-emerald-700">↗</span>
        Compensation Ladder
        {finalDelta != null && (
          <span
            className={`ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              finalDelta >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
            }`}
            data-testid="comp-ladder-final-delta"
          >
            {finalDelta >= 0 ? '+' : ''}₹{finalDelta.toFixed(1)} L
            {pctOfCurrent(finalDelta) != null && (
              <span className="ml-1 opacity-70">({pctOfCurrent(finalDelta)}%)</span>
            )}
          </span>
        )}
      </h4>
      <div className="flex items-stretch gap-1.5 overflow-x-auto">
        {steps.map((s, idx) => {
          const isEmpty = s.value <= 0;
          return (
            <React.Fragment key={s.key}>
              <div
                className={`flex-1 min-w-[72px] ${compact ? 'px-2 py-1.5' : 'px-3 py-2'} rounded-md ${
                  isEmpty
                    ? 'bg-white border border-dashed border-slate-200 text-slate-300'
                    : STEP_COLOR[s.key]
                }`}
                data-testid={`comp-ladder-step-${s.key}`}
              >
                <div className="text-[10px] uppercase tracking-wide opacity-70">
                  {s.label}
                </div>
                <div className={`font-semibold ${compact ? 'text-xs' : 'text-sm'} leading-tight mt-0.5`}>
                  {isEmpty ? '—' : `₹${s.value.toFixed(1)} L`}
                </div>
              </div>
              {idx < steps.length - 1 && (
                <div className="flex items-center text-slate-300 text-xs select-none">
                  →
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {(expectedDelta != null || offeredVsExpected != null) && !compact && (
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
          {expectedDelta != null && (
            <span data-testid="comp-ladder-hike-ask">
              Hike ask:{' '}
              <span
                className={`font-semibold ${
                  expectedDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'
                }`}
              >
                {expectedDelta >= 0 ? '+' : ''}₹{expectedDelta.toFixed(1)} L
              </span>
            </span>
          )}
          {offeredVsExpected != null && (
            <span data-testid="comp-ladder-offer-vs-expected">
              Offer vs expected:{' '}
              <span
                className={`font-semibold ${
                  offeredVsExpected >= 0 ? 'text-emerald-700' : 'text-amber-700'
                }`}
              >
                {offeredVsExpected >= 0 ? '+' : ''}₹{offeredVsExpected.toFixed(1)} L
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CompensationLadder;
